import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export type EmployeeSummary = {
  id: string;
  name: string;
  position?: string | null;
  department?: {
    _id: string;
    name: string;
  } | null;
  profileImage?: string | null;
};

export type AttendanceRecord = {
  recordId?: string; // AttendanceRecord id (for row Edit/Delete)
  employee: EmployeeSummary;
  status: string;
  checkIn?: string | null;
  checkOut?: string | null;
  breakMinutes?: number;
  lateMinutes?: number;
  productionHours?: number;
  isLate?: boolean;
  notes?: string | null;
  isOvertime?: boolean;
  isOvertimeApproved?: boolean;
  isHalfDay?: boolean;
  day?: string;
  overtimeMinutes?:number;
  overtimeRate?:number;
  calculatedOvertime?:number
};

export type AttendancePagination = {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  recordsPerPage: number;
};

export type AttendanceFilters = {
  startDate?: string | null;
  endDate?: string | null;
  department?: string | null;
  status?: string | null;
  sortBy?: string | null;
  search?: string | null;
};

export type AttendanceResponse = {
  success: boolean;
  data: {
    records: AttendanceRecord[];
    pagination: AttendancePagination;
    filters?: AttendanceFilters;
  };
  message?: string;
};

const fetchAttendance = async (params: {
  startDate?: string | null;
  endDate?: string | null;
  department?: string | null;
  status?: string | null;
  sortBy?: string | null;
  page?: number;
  limit?: number;
  search?: string | null;
}): Promise<AttendanceResponse> => {
  const {
    startDate,
    endDate,
    department,
    status,
    sortBy,
    page = 1,
    limit = 10,
    search,
  } = params;

  const qs = new URLSearchParams();

  if (startDate) qs.append("startDate", startDate);
  if (endDate) qs.append("endDate", endDate);
  if (department) qs.append("department", department);
  if (status) qs.append("status", status);
  if (sortBy) qs.append("sortBy", sortBy);
  qs.append("page", String(page));
  qs.append("limit", String(limit));
  if (search) qs.append("search", search);

  const url = `/reports/attendance/table?${qs.toString()}`;

  const resp = await customAxios.get<AttendanceResponse>(url);
  return resp.data;
};

export const useGetAttendanceReport = (params: {
  startDate?: string | null;
  endDate?: string | null;
  department?: string | null;
  status?: string | null;
  sortBy?: string | null;
  page?: number;
  limit?: number;
  search?: string | null;
}) =>
  useQuery<AttendanceResponse, AxiosError>(
    [
      "attendanceReport",
      params.startDate ?? "",
      params.endDate ?? "",
      params.department ?? "",
      params.status ?? "",
      params.sortBy ?? "",
      params.page ?? 1,
      params.limit ?? 10,
      params.search ?? "",
    ],
    () => fetchAttendance(params),
    {
      onError: (error) =>
        showNotification({
          title: "Error Loading Attendance",
          message:
            (error.response?.data as { message?: string })?.message ??
            error.message ??
            "Failed to load attendance",
          color: "red",
        }),
      staleTime: 0,
      retry: 1,
      refetchOnMount: "always",
    }
  );

export default useGetAttendanceReport;
